/**
 * Browser-safe Cloudflare Stream thumbnail helper (frontend).
 *
 * Raw `videodelivery.net` thumbnail URLs fail the browser TLS handshake
 * (ERR_SSL_VERSION_OR_CIPHER_MISMATCH) and render as blank tiles. The
 * customer-subdomain host (`customer-<code>.cloudflarestream.com`) has
 * browser-valid TLS. The account's customer code is delivered by
 * `/api/site-config` and set here once at app start (App.js). We also cache it
 * in localStorage so it's available synchronously on later loads, before
 * site-config resolves.
 *
 * SAFETY: toBrowserSafeThumb() is a pure host rewrite that is a NO-OP when the
 * code is unknown or the URL isn't a videodelivery.net one — so wrapping any
 * existing thumbnail URL can only improve it, never change behavior otherwise.
 */

const LS_KEY = 'cf_customer_code';

/**
 * Normalize to the BARE customer code. A full host
 * ("customer-<code>.cloudflarestream.com") or URL would otherwise double-wrap
 * into "customer-customer-….cloudflarestream.com.cloudflarestream.com", which
 * fails browser TLS and blanks every thumbnail. No-op on an already-bare code.
 */
function normalizeCustomerCode(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  const m = trimmed.match(/customer-([^./\s]+)\.cloudflarestream\.com/i);
  if (m) return m[1];
  return trimmed.replace(/^customer-/i, '').replace(/\.cloudflarestream\.com.*$/i, '');
}

let customerCode = '';
try {
  // Normalize on read too — a bad value may already be persisted from an
  // earlier session before this guard existed.
  customerCode = normalizeCustomerCode(
    (typeof localStorage !== 'undefined' && localStorage.getItem(LS_KEY)) || ''
  );
} catch (_) {
  /* localStorage unavailable (SSR / private mode) — fall back to empty */
}

/** Set + persist the account's Cloudflare Stream customer code. */
export function setCloudflareCustomerCode(code) {
  const normalized = normalizeCustomerCode(code);
  if (!normalized) return;
  customerCode = normalized;
  try {
    localStorage.setItem(LS_KEY, normalized);
  } catch (_) {
    /* ignore persistence failure — in-memory value still applies this session */
  }
}

/** Current known customer code ('' if unknown). */
export function getCloudflareCustomerCode() {
  return customerCode;
}

/**
 * Rewrite a legacy `videodelivery.net` URL (thumbnail or otherwise) to the
 * browser-valid customer-subdomain host. No-op when the code is unknown or the
 * URL isn't a videodelivery.net one. Host-only swap — path + query preserved.
 */
export function toBrowserSafeThumb(url) {
  if (!url || !customerCode) return url;
  return url.replace(
    /^https?:\/\/videodelivery\.net\//i,
    `https://customer-${customerCode}.cloudflarestream.com/`
  );
}
